import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  GoogleAuthProvider,
  setPersistence,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, initializeFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';

import { assertTestPreview, commerceTestPreview, testDatabaseId, testFirebaseAppName } from './commerce-test-preview';

// Firebase設定（環境変数から取得）
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || '',
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '',
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || '',
};

const emulatorFlagEnabled =
  process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === 'true' ||
  process.env.NEXT_PUBLIC_FIREBASE_EMULATOR === 'true';
const isLocalhostRuntime = (): boolean => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
};
const shouldUseEmulator = emulatorFlagEnabled && isLocalhostRuntime();

// ビルド時（サーバーサイド）ではFirebaseを初期化しない
// 有効なAPIキーがある場合のみ初期化
const shouldInitializeFirebase =
  typeof window !== 'undefined' || // クライアントサイド
  (process.env.NEXT_PUBLIC_FIREBASE_API_KEY &&
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY !== '' &&
    process.env.NEXT_PUBLIC_FIREBASE_API_KEY !== 'placeholder');

// Initialize Firebase (条件付き)
let app: ReturnType<typeof initializeApp> | null = null;
let auth: ReturnType<typeof getAuth> | null = null;
let db: ReturnType<typeof getFirestore> | null = null;
let functions: ReturnType<typeof getFunctions> | null = null;

if (shouldInitializeFirebase) {
  try {
    assertTestPreview();
    // 既に初期化されている場合は再利用
    const existingApps = getApps();
    if (commerceTestPreview) {
      const existing = existingApps.find(candidate => candidate.name === testFirebaseAppName);
      if (existing && Object.entries(firebaseConfig).some(([key, value]) =>
        existing.options[key as keyof typeof firebaseConfig] !== value)) {
        throw new Error('検証用Firebase接続の設定が一致しません。');
      }
      app = existing ?? initializeApp(firebaseConfig, testFirebaseAppName);
    } else if (existingApps.some(candidate => candidate.name === '[DEFAULT]')) {
      app = getApp();
    } else {
      app = initializeApp(firebaseConfig);
    }
    auth = getAuth(app);
    if (commerceTestPreview) auth.tenantId = process.env.NEXT_PUBLIC_COMMERCE_TEST_TENANT_ID || null;
    db = shouldUseEmulator
      ? initializeFirestore(app, { experimentalForceLongPolling: true }, commerceTestPreview ? testDatabaseId : undefined)
      : commerceTestPreview ? getFirestore(app, testDatabaseId) : getFirestore(app);
    // The isolated commerce test runtime is deployed separately in Tokyo
    // (asia-northeast1); all ordinary environments remain on asia-northeast2.
    functions = getFunctions(app, commerceTestPreview ? 'asia-northeast1' : 'asia-northeast2');
  } catch (error) {
    // ビルド時や無効な設定の場合はエラーを無視
    if (typeof window !== 'undefined') {
      console.warn('Firebase initialization failed:', error);
    }
  }
}

/**
 * Firestore の 1 文書を REST API で指す URL。SDK の書き込みはタブを閉じる瞬間には完了しない
 * ので、閉じる瞬間に送る退室だけは fetch の keepalive でこの URL に送る。接続先(エミュレータ・
 * 検証用データベース)は SDK と揃える。
 */
export const firestoreRestDocumentUrl = (path: string): string | null => {
  if (!db || !firebaseConfig.projectId) return null;
  const database = commerceTestPreview ? testDatabaseId : '(default)';
  const origin = shouldUseEmulator
    ? `http://${window.location.hostname === 'localhost' ? 'localhost' : '127.0.0.1'}:8083`
    : 'https://firestore.googleapis.com';
  return `${origin}/v1/projects/${firebaseConfig.projectId}/databases/${database}/documents/${path}`;
};

// Firebase services (nullの可能性があるため、使用時にチェックが必要)
export { app, auth, db, functions };

// ヘルパー関数: クライアントサイドでのみ使用（型安全性のため）
export const getAuthInstance = () => {
  assertTestPreview();
  if (!auth) {
    throw new Error(
      'Firebase Auth is not initialized. This should only happen on the server side.'
    );
  }
  return auth;
};

export const getDbInstance = () => {
  assertTestPreview();
  if (!db) {
    throw new Error(
      'Firebase Firestore is not initialized. This should only happen on the server side.'
    );
  }
  return db;
};

export const getFunctionsInstance = () => {
  assertTestPreview();
  if (!functions) {
    throw new Error(
      'Firebase Functions is not initialized. This should only happen on the server side.'
    );
  }
  return functions;
};

export const getGoogleProviderInstance = () => {
  if (!googleProvider) {
    throw new Error(
      'Google Auth Provider is not initialized. This should only happen on the server side.'
    );
  }
  return googleProvider;
};

// エミュレータ接続設定
// エミュレータ接続状態を追跡するフラグ
let isEmulatorConnected = false;

// クライアントサイドでエミュレータに接続
if (shouldUseEmulator && typeof window !== 'undefined' && auth && db && functions) {
  if (!isEmulatorConnected) {
    try {
      // Keep the emulator on the page's loopback hostname for browser local-network requests.
      const firestoreHost = window.location.hostname === 'localhost' ? 'localhost' : '127.0.0.1';
      connectFirestoreEmulator(db, firestoreHost, 8083);
      // CRITICAL: localhostではなく127.0.0.1を使用（DNSルックアップを回避し、ポート間接続の問題を解決）
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      isEmulatorConnected = true;
    } catch (error) {
      // エラーログは開発時のみ
      if (process.env.NODE_ENV === 'development') {
        console.warn('Emulator connection failed:', error);
      }
    }
  }
}

if (typeof window !== 'undefined' && emulatorFlagEnabled && !isLocalhostRuntime()) {
  console.warn('Firebase emulator flags are enabled, but current host is not localhost. Ignoring emulator connection.');
}

// Firebase認証の永続化を設定（クライアントサイドのみ）
if (typeof window !== 'undefined' && auth) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.error('Failed to set auth persistence:', error);
  });
}

// エミュレータ使用確認
export const isEmulatorEnabled = (): boolean => {
  return shouldUseEmulator && typeof window !== 'undefined';
};

// Google認証プロバイダー
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account',
});
// OAuthクレデンシャルフォールバック用に必要スコープを付与
googleProvider.addScope('openid');
googleProvider.addScope('profile');
googleProvider.addScope('email');

export default app;
